import React, { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { MenuTop } from '@components/layout/src/components/menu-top/menu-top';
import { MenuTopSkeleton } from '@components/layout/src/components/menu-top/menu-top-skeleton';
import { Modal } from '@components/modals/modal';
import { SidebarDesktop } from '@components/sidebar-desktop/src/sidebar-desktop';
import { SidebarDesktopSkeleton } from '@components/sidebar-desktop/src/sidebar-desktop-skeleton';
import { SidebarMobile } from '@components/sidebar-mobile/src/sidebar-mobile';
import { CLUB_CHANGE_CLUB_ROUTE, SELECT_VENUE_ROUTE } from '@constants/routes';
import { LG_VIEWPORT, XS_VIEWPORT } from '@constants/viewport';
import { CompanyClubContext } from '@contexts/companyClub/companyClub-context';
import { FeatureFlagContext } from '@contexts/featureFlag/featureFlag-context';
import { UserContext } from '@contexts/user/user-context';
import { useTokenManagement } from '@hooks/auth/use-token-management';
import { useCurrentEvent } from '@hooks/events/use-current-event';
import { useAndroidSwipeRefresh } from '@hooks/use-android-swipe-refresh/use-android-swipe-refresh';
import { useWindowSize } from '@hooks/use-window-size';
import { getOldNightgraphUrlLanguage } from '@utils/get-old-nightgraph-url-language/get-old-nightgraph-url-language';
import classNames from 'classnames';

import { MobileMenu } from '../mobile-menu';
import { SelectCompany } from '../select-company/select-company';

export const LayoutDefault = ({ children }: { children: React.ReactNode }) => {
  useAndroidSwipeRefresh();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMenuOpenInMobile, setIsMenuOpenInMobile] = useState(false);
  const [isOpenChangeCompanyModal, setIsOpenChangeCompanyModal] = useState<boolean>(false);
  const { clubState, companyState, isLoadingCompanyClub } = useContext(CompanyClubContext);
  const { isSelectVenueActive } = useContext(FeatureFlagContext);
  const { event } = useCurrentEvent();
  const { userState } = useContext(UserContext);
  const { getIsAdmin } = useTokenManagement();
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobileViewPort = useWindowSize(XS_VIEWPORT);
  const isTabletViewport = useWindowSize(LG_VIEWPORT);

  const locale = i18n.language;
  const old_nightgraph_locale = getOldNightgraphUrlLanguage(locale);
  const isChangeCompanyVisible = getIsAdmin();

  const showMobileMenu =
    isMobileViewPort &&
    !event?.name &&
    !location.pathname.includes('/offers/list') &&
    !location.pathname.includes('/insights');

  const locationPath = location.pathname.split('/');

  const handleIsVisible = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleIsMenuOpenInMobile = () => {
    setIsMenuOpenInMobile(!isMenuOpenInMobile);
  };

  const closeMenuMobile = useCallback(() => {
    setIsMenuOpenInMobile(false);
  }, [setIsMenuOpenInMobile]);

  const navigateTo = (path: string) => {
    navigate(`${locationPath[1]}/${locationPath[2]}/${path}`);
  };

  const nagitateToSettings = () => {
    isSelectVenueActive
      ? navigate(SELECT_VENUE_ROUTE)
      : window.open(CLUB_CHANGE_CLUB_ROUTE.replace('/:lang', old_nightgraph_locale), '_self');
  };

  return (
    <>
      <div
        data-main-scroll
        className={classNames('flex flex-col h-[calc(100dvh)] bg-secondary overflow-auto', {
          'pb-12 xs:pb-0': showMobileMenu,
        })}>
        {!isLoadingCompanyClub && (
          <div className='hidden xs:flex lg:hidden'>
            <SidebarMobile
              isMenuOpenInMobile={isMenuOpenInMobile}
              handleIsMenuOpenInMobile={handleIsMenuOpenInMobile}
              closeMenuMobile={closeMenuMobile}
              handleCompanyModal={isChangeCompanyVisible ? setIsOpenChangeCompanyModal : nagitateToSettings}
            />
          </div>
        )}
        {isLoadingCompanyClub ? <MenuTopSkeleton /> : <MenuTop handleIsMenuOpenInMobile={handleIsMenuOpenInMobile} />}
        <div className='flex flex-row flex-1 overflow-hidden'>
          <div className='hidden lg:flex'>
            {isLoadingCompanyClub ? (
              <SidebarDesktopSkeleton />
            ) : (
              <SidebarDesktop
                isCollapsed={isCollapsed}
                handleIsVisible={isTabletViewport || isMobileViewPort ? undefined : handleIsVisible}
                handleCompanyModal={isChangeCompanyVisible ? setIsOpenChangeCompanyModal : nagitateToSettings}
              />
            )}
          </div>
          <main
            className={classNames(
              'flex flex-col w-full overflow-hidden bg-secondary transition-all duration-200',
              'mx-auto'
            )}>
            {children}
          </main>
        </div>
        {showMobileMenu && !isLoadingCompanyClub && <MobileMenu navigateTo={navigateTo} location={location} />}
      </div>
      {isMobileViewPort && !isLoadingCompanyClub && (
        <Modal isOpen={isMenuOpenInMobile && !isOpenChangeCompanyModal} handleClose={closeMenuMobile} hasMobileHandle>
          <div className='flex flex-col h-full'>
            <SidebarDesktop
              isCollapsed={false}
              handleIsVisible={undefined}
              handleCompanyModal={isChangeCompanyVisible ? setIsOpenChangeCompanyModal : nagitateToSettings}
            />
          </div>
        </Modal>
      )}
      <Modal isOpen={isOpenChangeCompanyModal} handleClose={() => setIsOpenChangeCompanyModal(false)} variant='md'>
        <SelectCompany
          companySelected={companyState}
          user={userState}
          handleClose={() => setIsOpenChangeCompanyModal(false)}
          clubState={clubState}
        />
      </Modal>
    </>
  );
};
